import api from '../api/client';

// Practical Report Types
export interface BirdCertificate {
  bird: {
    id: string;
    bandNumber: string;
    species: {
      commonName: string;
      scientificName: string;
      family: string;
      order: string;
      conservationStatus: string;
      citesStatus: string;
      averageLifespan: string;
      nativeHabitat: string;
    };
    identification: {
      bandId: string;
      microchipId?: string;
      dnaProfile?: string;
      distinguishingMarks?: string;
    };
    biologicalInfo: {
      sex: string;
      sexDeterminationMethod: string;
      hatchDate: string;
      bandingDate?: string;
      age: string;
      ageInMonths: number;
      weight?: number;
      wingspan?: number;
      colorMutation: string;
      phenotype: string;
      genotype: string;
    };
    status: {
      current: string;
      location: string;
      health: string;
      fertility: string;
      breeding: string;
    };
    provenance: {
      type: string;
      origin: string;
      dateAcquired: string;
      sellerName?: string;
      sellerContact?: string;
      sellerAddress?: string;
      purchasePrice?: number;
      currency: string;
      purchaseOrderNumber?: string;
      invoiceNumber?: string;
      paymentMethod?: string;
      paymentReference?: string;
      transactionId?: string;
      batchNumber?: string;
      warrantyInfo?: string;
      healthCertificateNumber?: string;
      importPermitNumber?: string;
      microchipId?: string;
      quarantinePeriod?: number;
      quarantineStartDate?: string;
      quarantineEndDate?: string;
      quarantineStatus: string;
      notes?: string;
    };
    lineage: {
      father?: {
        id: string;
        bandNumber: string;
        species: {
          commonName: string;
          scientificName: string;
        };
        birthYear?: number;
        age?: string;
        hatchingDate?: string;
        status?: string;
      };
      mother?: {
        id: string;
        bandNumber: string;
        species: {
          commonName: string;
          scientificName: string;
        };
        birthYear?: number;
        age?: string;
        hatchingDate?: string;
        status?: string;
      };
      siblings: Array<{
        id: string;
        bandNumber: string;
        sex: string;
        species: {
          commonName: string;
          scientificName: string;
        };
        birthYear?: number;
        age?: string;
      }>;
      offspring: Array<{
        id: string;
        bandNumber: string;
        sex: string;
        species: {
          commonName: string;
          scientificName: string;
        };
        birthYear?: number;
        age?: string;
        hatchingDate?: string;
      }>;
    };
    healthRecords: {
      lastExamination?: {
        date: string;
        testType: string;
        result: string;
        veterinarian: string;
      };
      vaccinationStatus: string;
      testResults: Array<{
        date: string;
        testType: string;
        result: string;
        veterinarian: string;
        notes?: string;
      }>;
      healthConcerns: string[];
    };
    photos: Array<{
      id: string;
      url: string;
      caption?: string;
      dateAdded: string;
    }>;
    valuation: {
      estimatedValue: number;
      currency: string;
      valuationDate: string;
      factors: string[];
    };
    breeder: {
      facilityName: string;
      licenseNumber: string;
      address: string;
      phone: string;
      email: string;
      website?: string;
      registrationBody: string;
      membershipNumber: string;
    };
  };
  certificateNumber: string;
  issueDate: string;
  validUntil: string;
}

export interface BirdListing {
  birds: Array<{
    id: string;
    bandNumber: string;
    species: {
      commonName: string;
      scientificName: string;
    };
    sex: string;
    hatchDate: string;
    age: string;
    status: string;
    location: string;
    value: number;
    lastHealthCheck?: string;
  }>;
  totalCount: number;
  totalValue: number;
  reportDate: string;
}

export interface BreedingList {
  breedingPairs: Array<{
    id: string;
    pairName: string;
    male: {
      id: string;
      bandNumber: string;
      species: string;
    };
    female: {
      id: string;
      bandNumber: string;
      species: string;
    };
    pairedDate: string;
    status: string;
    clutches: Array<{
      id: string;
      startDate: string;
      eggCount: number;
      hatchedCount: number;
      status: string;
    }>;
    totalClutches: number;
    totalEggs: number;
    totalHatched: number;
    successRate: number;
  }>;
  reportDate: string;
  activePairs: number;
  totalPairs: number;
}

export interface BalanceSheet {
  reportDate: string;
  period: string;
  assets: {
    currentAssets: {
      cash: number;
      inventory: number;
      accountsReceivable: number;
      birdStock: number;
      feedStock: number;
      supplies: number;
      total: number;
    };
    fixedAssets: {
      equipment: number;
      aviaries: number;
      breedingCages: number;
      landBuildings: number;
      total: number;
    };
    totalAssets: number;
  };
  liabilities: {
    currentLiabilities: {
      accountsPayable: number;
      shortTermDebt: number;
      accruedExpenses: number;
      total: number;
    };
    longTermLiabilities: {
      longTermDebt: number;
      total: number;
    };
    totalLiabilities: number;
  };
  equity: {
    ownerEquity: number;
    retainedEarnings: number;
    total: number;
  };
  totalLiabilitiesEquity: number;
}

export interface IncomeStatement {
  reportDate: string;
  period: string;
  revenue: {
    birdSales: number;
    breedingFees: number;
    consultingFees: number;
    other: number;
    total: number;
  };
  costOfGoodsSold: {
    feedCosts: number;
    veterinaryCosts: number;
    breedingCosts: number;
    other: number;
    total: number;
  };
  grossProfit: number;
  operatingExpenses: {
    facilitiesMaintenance: number;
    utilities: number;
    insurance: number;
    labor: number;
    marketing: number;
    administrative: number;
    depreciation: number;
    other: number;
    total: number;
  };
  operatingIncome: number;
  otherIncome: number;
  otherExpenses: number;
  netIncome: number;
  grossProfitMargin: number;
  netProfitMargin: number;
}

export interface InventoryReport {
  reportDate: string;
  birdInventory: {
    bySpecies: Array<{
      species: string;
      count: number;
      averageValue: number;
      totalValue: number;
      status: {
        breeding: number;
        forSale: number;
        personal: number;
        other: number;
      };
    }>;
    totalBirds: number;
    totalValue: number;
  };
  feedInventory: {
    items: Array<{
      name: string;
      type: string;
      currentStock: number;
      unit: string;
      costPerUnit: number;
      totalValue: number;
      lastRestocked: string;
      reorderLevel: number;
      status: 'in-stock' | 'low-stock' | 'out-of-stock';
    }>;
    totalValue: number;
  };
  suppliesInventory: {
    items: Array<{
      name: string;
      category: string;
      currentStock: number;
      unit: string;
      costPerUnit: number;
      totalValue: number;
      lastRestocked: string;
      status: 'in-stock' | 'low-stock' | 'out-of-stock';
    }>;
    totalValue: number;
  };
  grandTotalValue: number;
}

export interface ReportFilters {
  startDate?: string;
  endDate?: string;
  speciesId?: string;
  locationId?: string;
  status?: string;
  includeInactive?: boolean;
}

class ReportsService {
  // Bird Certificate
  async getBirdCertificate(birdId: string): Promise<BirdCertificate> {
    const response = await api.get<BirdCertificate>(`reports/bird-certificate/${birdId}`);
    return response.data;
  }

  // Birds Listing
  async getBirdsListing(filters?: ReportFilters): Promise<BirdListing> {
    const params: Record<string, string> = {};
    if (filters?.startDate) params.startDate = filters.startDate;
    if (filters?.endDate) params.endDate = filters.endDate;
    if (filters?.speciesId) params.speciesId = filters.speciesId;
    if (filters?.locationId) params.locationId = filters.locationId;
    if (filters?.status) params.status = filters.status;
    if (filters?.includeInactive) params.includeInactive = 'true';

    const response = await api.get<BirdListing>('reports/birds-listing', params);
    return response.data;
  }

  // Breeding List
  async getBreedingList(filters?: ReportFilters): Promise<BreedingList> {
    const params: Record<string, string> = {};
    if (filters?.startDate) params.startDate = filters.startDate;
    if (filters?.endDate) params.endDate = filters.endDate;
    if (filters?.speciesId) params.speciesId = filters.speciesId;
    if (filters?.includeInactive) params.includeInactive = 'true';

    const response = await api.get<BreedingList>('reports/breeding-list', params);
    return response.data;
  }

  // Balance Sheet
  async getBalanceSheet(asOfDate?: string): Promise<BalanceSheet> {
    const params: Record<string, string> = {};
    if (asOfDate) params.asOfDate = asOfDate;

    const response = await api.get<BalanceSheet>('reports/balance-sheet', params);
    return response.data;
  }

  // Income Statement
  async getIncomeStatement(filters?: ReportFilters): Promise<IncomeStatement> {
    const params: Record<string, string> = {};
    if (filters?.startDate) params.startDate = filters.startDate;
    if (filters?.endDate) params.endDate = filters.endDate;

    const response = await api.get<IncomeStatement>('reports/income-statement', params);
    return response.data;
  }

  // Inventory Report
  async getInventoryReport(asOfDate?: string): Promise<InventoryReport> {
    const params: Record<string, string> = {};
    if (asOfDate) params.asOfDate = asOfDate;

    const response = await api.get<InventoryReport>('reports/inventory-report', params);
    return response.data;
  }
}

export const reportsService = new ReportsService(); 